import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  excludeKeys,
  generateElementComponentsFromSchemas,
  generateCategoryHash,
} from '../utils';
import Card from '../Card';
import Section from '../Section';
import FBCheckbox from '../FBCheckbox';
import shortAnswerInputs from './shortAnswerInputs';
import longAnswerInputs from './longAnswerInputs';
import numberInputs from './numberInputs';
import defaultInputs from './defaultInputs';
import { getRandomId } from '../utils';
import type { FormInput, CardComponent, CardComponentProps } from '../types';

// specify the inputs required for a string type object
const CardArrayParameterInputs: CardComponent = ({ parameters, onChange }) => {
  return (
    <Box>
      <Typography variant='subtitle2' fontWeight='bold'>
        Minimum Items
      </Typography>
      <TextField
        value={parameters.minItems || ''}
        placeholder='ex: 2'
        type='number'
        onChange={(ev) => {
          onChange({
            ...parameters,
            minItems: parseInt(ev.target.value, 10),
          });
        }}
        size='small'
        fullWidth
        sx={{ mb: 2 }}
      />
      <Typography variant='subtitle2' fontWeight='bold'>
        Maximum Items
      </Typography>
      <TextField
        value={parameters.maxItems || ''}
        placeholder='ex: 2'
        type='number'
        onChange={(ev) => {
          onChange({
            ...parameters,
            maxItems: parseInt(ev.target.value, 10),
          });
        }}
        size='small'
        fullWidth
        sx={{ mb: 2 }}
      />
    </Box>
  );
};

const InnerCard: CardComponent = ({ parameters, onChange, mods }) => {
  const [elementId] = useState(getRandomId);
  const newDataProps: { [key: string]: any } = {};
  const newUiProps: { [key: string]: any } = {};
  const allFormInputs = excludeKeys(
    Object.assign({}, defaultFormInputs, (mods && mods.customFormInputs) || {}),
    mods && mods.deactivatedFormInputs,
  );

  // parse components into data and ui relevant pieces
  Object.keys(parameters).forEach((propName: string) => {
    if (propName.startsWith('ui:*')) {
      newUiProps[propName.substring(4)] =
        parameters[propName as keyof CardComponentProps];
    } else if (propName.startsWith('ui:')) {
      newUiProps[propName] = parameters[propName as keyof CardComponentProps];
    } else if (!['name', 'required'].includes(propName)) {
      newDataProps[propName] = parameters[propName as keyof CardComponentProps];
    }
  });

  const definitionData = parameters.definitionData
    ? parameters.definitionData
    : {};
  const definitionUi = parameters.definitionUi ? parameters.definitionUi : {};
  const [cardOpen, setCardOpen] = React.useState(false);
  if (parameters.type !== 'array') {
    return (
      <Typography variant='body2' color='error'>
        Not an array
      </Typography>
    );
  }
  return (
    <>
      <FBCheckbox
        onChangeValue={() => {
          if (newDataProps.items.type === 'object') {
            onChange({
              ...parameters,
              items: {
                ...newDataProps.items,
                type: 'string',
              },
            });
          } else {
            onChange({
              ...parameters,
              items: {
                ...newDataProps.items,
                type: 'object',
              },
            });
          }
        }}
        isChecked={newDataProps.items.type === 'object'}
        label='Section'
      />
      {generateElementComponentsFromSchemas({
        schemaData: { properties: { item: newDataProps.items } },
        uiSchemaData: { item: newUiProps.items },
        onChange: (schema, uischema) => {
          onChange({
            ...parameters,
            items: schema.properties.item,
            'ui:*items': uischema.item || {},
          });
        },
        path: elementId,
        definitionData,
        definitionUi,
        hideKey: true,
        cardOpenArray: [cardOpen],
        setCardOpenArray: (updater) => {
          const newArr =
            typeof updater === 'function' ? updater([cardOpen]) : updater;
          setCardOpen(newArr[0]);
        },
        allFormInputs,
        mods,
        categoryHash: generateCategoryHash(allFormInputs),
        Card: (props) => <Card {...props} showObjectNameInput={false} />,
        Section,
      })}
    </>
  );
};

function getInnerCardComponent(): CardComponent {
  return InnerCard;
}

const defaultFormInputs: { [key: string]: FormInput } = {
  ...defaultInputs,
  ...shortAnswerInputs,
  ...longAnswerInputs,
  ...numberInputs,
};
defaultFormInputs.array = {
  displayName: 'Array',
  matchIf: [
    {
      types: ['array'],
    },
  ],
  defaultDataSchema: {
    items: { type: 'string' },
  },
  defaultUiSchema: {},
  type: 'array',
  cardBody: getInnerCardComponent(),
  modalBody: CardArrayParameterInputs,
};

const ArrayInputs: { [key: string]: FormInput } = {
  array: {
    displayName: 'Array',
    matchIf: [
      {
        types: ['array'],
      },
    ],
    defaultDataSchema: {
      items: { type: 'string' },
    },
    defaultUiSchema: {},
    type: 'array',
    cardBody: getInnerCardComponent(),
    modalBody: CardArrayParameterInputs,
  },
};

export default ArrayInputs;
